/**
 * Text written by a model, an agent or a person (memory answers, Microsoft 365's answers, chat,
 * task comments, descriptions, release notes) as the Markdown it is written in. GitHub-flavoured
 * (tables, task lists, strikethrough, bare links). `breaks` keeps a single line break as a break, for
 * conversation (chat, answers, notifications); files written with hard wraps (release notes, roadmaps)
 * leave it off, as GitHub does.
 *
 * Safe by construction: raw HTML in the text is shown as text, never rendered; links open in a new
 * tab without the opener; an image is a link to it, never fetched, so a reply cannot make the
 * console call out to an address it names. Long links and identifiers wrap instead of running off
 * the card.
 */

import type { ReactNode } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';

const components: Components = {
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noreferrer noopener">
      {children}
    </a>
  ),
  img: ({ src, alt }) =>
    typeof src === 'string' && src ? (
      <a href={src} target="_blank" rel="noreferrer noopener">
        {alt || 'Image'}
      </a>
    ) : (
      <>{alt}</>
    ),
};

/** `compact` for small print (descriptions, notes in a list): smaller type, tighter spacing. */
export function Markdown({ text, className, compact, breaks, fallback }: { text: string | undefined | null; className?: string; compact?: boolean; breaks?: boolean; fallback?: ReactNode }) {
  if (!text?.trim()) return fallback ? <>{fallback}</> : null;
  return (
    <div className={`md-body${compact ? ' md-compact' : ''}${className ? ` ${className}` : ''}`}>
      <ReactMarkdown remarkPlugins={breaks ? [remarkGfm, remarkBreaks] : [remarkGfm]} components={components}>
        {text}
      </ReactMarkdown>
    </div>
  );
}

/** The same text on one line, its Markdown marks taken out (shared with the mobile app). */
export { plainText } from '@aioe/shared';
